import { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { Edit, Eye, FileText, Plus, ReceiptText, Search, Trash2 } from 'lucide-react';
import { ERPUser } from '../../core/auth/types';
import { db } from '../../core/firebase/firebase';

type PaymentMode = 'cash' | 'zelle' | 'venmo' | 'check' | 'credit_card' | 'bank_transfer' | 'other';
type BookingStatus = 'draft' | 'confirmed' | 'cancelled' | 'completed';
type PaymentStatus = 'unpaid' | 'partial' | 'paid' | 'refunded';

type ClientOption = {
  id: string;
  name: string;
  phone: string;
  email: string;
};

type SupplierOption = {
  id: string;
  name: string;
  type: string;
  department: string;
  departments: string[];
  status: string;
};

type BookingRecord = {
  id: string;
  bookingCode: string;
  inquiryId: string;
  clientId: string;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  supplierId: string;
  supplierName: string;
  department: string;
  serviceType: string;
  subServiceType: string;
  destination: string;
  travelStartDate: string;
  travelEndDate: string;
  returnDate: string;
  passengers: number;
  sellAmount: number;
  netAmount: number;
  receivedAmount: number;
  supplierPaidAmount: number;
  refundAmount: number;
  profit: number;
  outstandingAmount: number;
  supplierBalance: number;
  bookingStatus: BookingStatus;
  paymentStatus: PaymentStatus;
  supplierPaymentStatus: PaymentStatus;
  paymentMode: PaymentMode;
  supplierPaymentMode: PaymentMode;
  paymentReference: string;
  supplierPaymentReference: string;
  invoiceStatus: string;
  receiptStatus: string;
  refundStatus: string;
  invoiceNo: string;
  receiptNo: string;
  refundNo: string;
  accountingStatus: string;
  paymentNotes: string;
  notes: string;
  createdBy: string;
  updatedBy: string;
};

const serviceTypes = ['flight', 'package', 'cruise', 'hotel', 'visa', 'insurance', 'transport', 'other'];
const departments = ['flight', 'package', 'cruise', 'account', 'general', 'visa', 'insurance', 'transport'];
const paymentModes: PaymentMode[] = ['cash', 'zelle', 'venmo', 'check', 'credit_card', 'bank_transfer', 'other'];

const subServiceMap: Record<string, string[]> = {
  flight: ['New Ticket', 'Date Change', 'Cancellation', 'Refund', 'Seat / Baggage', 'Name Correction', 'Other'],
  cruise: ['New Cruise Booking', 'Cabin Upgrade', 'Gratuity', 'Insurance', 'Excursion', 'Cancellation', 'Other'],
  package: ['Full Package', 'Hotel Only', 'Transfer', 'Sightseeing', 'Custom Package', 'Group Tour', 'Other'],
  hotel: ['Hotel Booking', 'Room Upgrade', 'Extra Night', 'Cancellation', 'Other'],
  visa: ['Visa Fee', 'Service Fee', 'Appointment', 'Document Assistance', 'Other'],
  insurance: ['Travel Insurance', 'Medical Insurance', 'Cancellation Insurance', 'Other'],
  transport: ['Airport Transfer', 'Private Vehicle', 'Bus / Coach', 'Train', 'Other'],
  other: ['Manual Service', 'Other']
};

function cleanDept(value: unknown) {
  return String(value || '').toLowerCase().replace(/[^a-z_]/g, '').trim();
}

function money(value: unknown) {
  const numericValue = Number(value === '' || value === null || value === undefined ? 0 : value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function makeCode(prefix: string) {
  return `${prefix}-${Math.floor(100000 + Math.random() * 900000)}`;
}

function paymentStatusFor(total: number, paid: number, refund = 0): PaymentStatus {
  if (refund > 0) return 'refunded';
  if (total > 0 && paid >= total) return 'paid';
  if (paid > 0) return 'partial';
  return 'unpaid';
}

function financials(source: Partial<BookingRecord> | Record<string, unknown>) {
  const sellAmount = money(source.sellAmount);
  const netAmount = money(source.netAmount);
  const receivedAmount = money(source.receivedAmount);
  const supplierPaidAmount = money(source.supplierPaidAmount);
  const refundAmount = money(source.refundAmount);
  const profit = sellAmount - netAmount - refundAmount;
  const outstandingAmount = Math.max(sellAmount - receivedAmount - refundAmount, 0);
  const supplierBalance = Math.max(netAmount - supplierPaidAmount, 0);

  return {
    sellAmount,
    netAmount,
    receivedAmount,
    supplierPaidAmount,
    refundAmount,
    profit,
    outstandingAmount,
    supplierBalance,
    paymentStatus: paymentStatusFor(sellAmount, receivedAmount, refundAmount),
    supplierPaymentStatus: paymentStatusFor(netAmount, supplierPaidAmount, 0)
  };
}

function normalizeBooking(id: string, raw: Record<string, any>): BookingRecord {
  const calculated = financials(raw);
  const serviceType = String(raw.serviceType || 'package');

  return {
    id,
    bookingCode: String(raw.bookingCode || makeCode('BK')),
    inquiryId: String(raw.inquiryId || ''),
    clientId: String(raw.clientId || ''),
    clientName: String(raw.clientName || ''),
    clientPhone: String(raw.clientPhone || ''),
    clientEmail: String(raw.clientEmail || ''),
    supplierId: String(raw.supplierId || ''),
    supplierName: String(raw.supplierName || ''),
    department: String(raw.department || 'package'),
    serviceType,
    subServiceType: String(raw.subServiceType || subServiceMap[serviceType]?.[0] || 'Other'),
    destination: String(raw.destination || ''),
    travelStartDate: String(raw.travelStartDate || ''),
    travelEndDate: String(raw.travelEndDate || ''),
    returnDate: String(raw.returnDate || ''),
    passengers: Number(raw.passengers || 1),
    sellAmount: calculated.sellAmount,
    netAmount: calculated.netAmount,
    receivedAmount: calculated.receivedAmount,
    supplierPaidAmount: calculated.supplierPaidAmount,
    refundAmount: calculated.refundAmount,
    profit: calculated.profit,
    outstandingAmount: calculated.outstandingAmount,
    supplierBalance: calculated.supplierBalance,
    bookingStatus: (raw.bookingStatus || 'draft') as BookingStatus,
    paymentStatus: (raw.paymentStatus || calculated.paymentStatus) as PaymentStatus,
    supplierPaymentStatus: (raw.supplierPaymentStatus || calculated.supplierPaymentStatus) as PaymentStatus,
    paymentMode: (raw.paymentMode || 'zelle') as PaymentMode,
    supplierPaymentMode: (raw.supplierPaymentMode || 'bank_transfer') as PaymentMode,
    paymentReference: String(raw.paymentReference || ''),
    supplierPaymentReference: String(raw.supplierPaymentReference || ''),
    invoiceStatus: String(raw.invoiceStatus || 'pending'),
    receiptStatus: String(raw.receiptStatus || (calculated.receivedAmount > 0 ? 'issued' : 'pending')),
    refundStatus: String(raw.refundStatus || (calculated.refundAmount > 0 ? 'processed' : 'none')),
    invoiceNo: String(raw.invoiceNo || ''),
    receiptNo: String(raw.receiptNo || ''),
    refundNo: String(raw.refundNo || ''),
    accountingStatus: String(raw.accountingStatus || 'open'),
    paymentNotes: String(raw.paymentNotes || ''),
    notes: String(raw.notes || ''),
    createdBy: String(raw.createdBy || ''),
    updatedBy: String(raw.updatedBy || '')
  };
}

function emptyBooking(): Partial<BookingRecord> {
  return {
    bookingCode: '',
    inquiryId: '',
    clientId: '',
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    supplierId: '',
    supplierName: '',
    department: 'package',
    serviceType: 'package',
    subServiceType: 'Full Package',
    destination: '',
    travelStartDate: '',
    travelEndDate: '',
    returnDate: '',
    passengers: 1,
    sellAmount: 0,
    netAmount: 0,
    receivedAmount: 0,
    supplierPaidAmount: 0,
    refundAmount: 0,
    bookingStatus: 'draft',
    paymentStatus: 'unpaid',
    supplierPaymentStatus: 'unpaid',
    paymentMode: 'zelle',
    supplierPaymentMode: 'bank_transfer',
    paymentReference: '',
    supplierPaymentReference: '',
    invoiceStatus: 'pending',
    receiptStatus: 'pending',
    refundStatus: 'none',
    invoiceNo: '',
    receiptNo: '',
    refundNo: '',
    accountingStatus: 'open',
    paymentNotes: '',
    notes: ''
  };
}

async function loadClients(): Promise<ClientOption[]> {
  const snapshot = await getDocs(collection(db, 'clients'));

  return snapshot.docs
    .map((clientDoc) => {
      const data = clientDoc.data() as Record<string, any>;
      const name = String(data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.name || clientDoc.id);

      return {
        id: clientDoc.id,
        name,
        phone: String(data.phoneFull || data.phoneNumber || data.phone || ''),
        email: String(data.email || '')
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

async function loadSuppliers(): Promise<SupplierOption[]> {
  const snapshot = await getDocs(collection(db, 'suppliers'));

  return snapshot.docs
    .map((supplierDoc) => {
      const data = supplierDoc.data() as Record<string, any>;
      const primaryDepartment = cleanDept(data.department || (Array.isArray(data.departments) ? data.departments[0] : '') || 'general');
      const supplierDepartments = Array.isArray(data.departments) && data.departments.length
        ? data.departments.map(cleanDept)
        : [primaryDepartment];

      return {
        id: supplierDoc.id,
        name: String(data.supplierName || data.companyName || data.name || supplierDoc.id),
        type: String(data.supplierType || data.type || ''),
        department: primaryDepartment,
        departments: supplierDepartments,
        status: String(data.status || 'active').toLowerCase()
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

async function loadBookings(): Promise<BookingRecord[]> {
  try {
    const snapshot = await getDocs(query(collection(db, 'bookings'), orderBy('updatedAt', 'desc')));
    return snapshot.docs.map((bookingDoc) => normalizeBooking(bookingDoc.id, bookingDoc.data() as Record<string, any>));
  } catch {
    const snapshot = await getDocs(collection(db, 'bookings'));
    return snapshot.docs.map((bookingDoc) => normalizeBooking(bookingDoc.id, bookingDoc.data() as Record<string, any>));
  }
}

function supplierMatchesDepartment(supplier: SupplierOption, department: string) {
  const selectedDepartment = cleanDept(department || 'general');
  if (supplier.status && supplier.status !== 'active') return false;
  return supplier.departments.includes(selectedDepartment) || supplier.department === selectedDepartment || supplier.departments.includes('general');
}

function userCanEditAccounting(user: ERPUser) {
  return user.role === 'super_admin' || user.role === 'admin' || user.department === 'account' || Boolean((user as any).canViewFinance) || Boolean((user as any).canEditBookings);
}

export default function BookingsPage({ user }: { user: ERPUser }) {
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState<BookingRecord | null>(null);
  const [editing, setEditing] = useState<BookingRecord | null>(null);
  const [form, setForm] = useState<Partial<BookingRecord>>(emptyBooking());
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function loadAll() {
    const [bookingRows, clientRows, supplierRows] = await Promise.all([
      loadBookings(),
      loadClients().catch(() => []),
      loadSuppliers().catch(() => [])
    ]);
    setBookings(bookingRows);
    setClients(clientRows);
    setSuppliers(supplierRows);
  }

  useEffect(() => {
    loadAll().catch((error) => setMessage(error instanceof Error ? error.message : 'Unable to load bookings.'));
  }, []);

  const filteredBookings = useMemo(() => {
    const queryText = search.toLowerCase();

    return bookings.filter((booking) => {
      if (!queryText) return true;
      return [
        booking.bookingCode,
        booking.clientName,
        booking.clientPhone,
        booking.supplierName,
        booking.serviceType,
        booking.subServiceType,
        booking.destination,
        booking.bookingStatus,
        booking.paymentStatus
      ].join(' ').toLowerCase().includes(queryText);
    });
  }, [bookings, search]);

  const filteredClients = useMemo(() => {
    const queryText = clientSearch.toLowerCase();
    return clients
      .filter((client) => !queryText || [client.name, client.phone, client.email].join(' ').toLowerCase().includes(queryText))
      .slice(0, 80);
  }, [clients, clientSearch]);

  const filteredSuppliers = useMemo(() => {
    const queryText = supplierSearch.toLowerCase();
    return suppliers
      .filter((supplier) => supplierMatchesDepartment(supplier, form.department || 'general'))
      .filter((supplier) => !queryText || [supplier.name, supplier.type, supplier.department].join(' ').toLowerCase().includes(queryText))
      .slice(0, 80);
  }, [suppliers, supplierSearch, form.department]);

  const subServices = useMemo(() => subServiceMap[form.serviceType || 'other'] || subServiceMap.other, [form.serviceType]);

  const totals = useMemo(() => ({
    sell: bookings.reduce((sum, item) => sum + item.sellAmount, 0),
    received: bookings.reduce((sum, item) => sum + item.receivedAmount, 0),
    outstanding: bookings.reduce((sum, item) => sum + item.outstandingAmount, 0),
    supplierBalance: bookings.reduce((sum, item) => sum + item.supplierBalance, 0),
    profit: bookings.reduce((sum, item) => sum + item.profit, 0)
  }), [bookings]);

  function startAdd() {
    setEditing(null);
    setViewing(null);
    setForm(emptyBooking());
    setClientSearch('');
    setSupplierSearch('');
    setMessage('');
    setOpen(true);
    loadAll();
  }

  function startEdit(booking: BookingRecord) {
    setEditing(booking);
    setViewing(null);
    setForm(booking);
    setClientSearch(booking.clientName || '');
    setSupplierSearch(booking.supplierName || '');
    setMessage('');
    setOpen(true);
    loadAll();
  }

  function selectClient(clientId: string) {
    const selectedClient = clients.find((client) => client.id === clientId);
    setForm({
      ...form,
      clientId,
      clientName: selectedClient?.name || '',
      clientPhone: selectedClient?.phone || '',
      clientEmail: selectedClient?.email || ''
    });
    if (selectedClient) setClientSearch(selectedClient.name);
  }

  function selectSupplier(supplierId: string) {
    const selectedSupplier = suppliers.find((supplier) => supplier.id === supplierId);
    setForm({
      ...form,
      supplierId,
      supplierName: selectedSupplier?.name || ''
    });
    if (selectedSupplier) setSupplierSearch(selectedSupplier.name);
  }

  function updateServiceType(serviceType: string) {
    const nextSubService = subServiceMap[serviceType]?.[0] || 'Other';
    const nextDepartment = serviceType === 'hotel'
      ? 'package'
      : serviceType === 'insurance'
        ? 'insurance'
        : serviceType === 'visa'
          ? 'visa'
          : serviceType === 'transport'
            ? 'transport'
            : serviceType;

    setForm({
      ...form,
      serviceType,
      subServiceType: nextSubService,
      department: nextDepartment,
      supplierId: '',
      supplierName: ''
    });
  }

  async function saveBooking() {
    if (!form.clientId) {
      setMessage('Client is required.');
      return;
    }

    if (!userCanEditAccounting(user)) {
      setMessage('You do not have permission to create or edit booking/accounting values.');
      return;
    }

    if (form.returnDate && form.travelStartDate && form.returnDate < form.travelStartDate) {
      setMessage('Return Date cannot be before Travel Start Date.');
      return;
    }

    const calculated = financials(form);
    const invoiceNo = form.invoiceNo || (calculated.sellAmount > 0 ? makeCode('INV') : '');
    const receiptNo = form.receiptNo || (calculated.receivedAmount > 0 ? makeCode('RCT') : '');
    const refundNo = form.refundNo || (calculated.refundAmount > 0 ? makeCode('RF') : '');

    const payload = {
      bookingCode: form.bookingCode || makeCode('BK'),
      inquiryId: form.inquiryId || '',
      clientId: form.clientId || '',
      clientName: form.clientName || '',
      clientPhone: form.clientPhone || '',
      clientEmail: form.clientEmail || '',
      supplierId: form.supplierId || '',
      supplierName: form.supplierName || '',
      department: form.department || 'package',
      serviceType: form.serviceType || 'package',
      subServiceType: form.subServiceType || subServiceMap[form.serviceType || 'package']?.[0] || 'Other',
      destination: form.destination || '',
      travelStartDate: form.travelStartDate || '',
      travelEndDate: form.travelEndDate || '',
      returnDate: form.returnDate || '',
      passengers: Number(form.passengers || 1),
      sellAmount: calculated.sellAmount,
      netAmount: calculated.netAmount,
      receivedAmount: calculated.receivedAmount,
      supplierPaidAmount: calculated.supplierPaidAmount,
      refundAmount: calculated.refundAmount,
      profit: calculated.profit,
      outstandingAmount: calculated.outstandingAmount,
      supplierBalance: calculated.supplierBalance,
      bookingStatus: form.bookingStatus || 'draft',
      paymentStatus: calculated.paymentStatus,
      supplierPaymentStatus: calculated.supplierPaymentStatus,
      paymentMode: form.paymentMode || 'zelle',
      supplierPaymentMode: form.supplierPaymentMode || 'bank_transfer',
      paymentReference: form.paymentReference || '',
      supplierPaymentReference: form.supplierPaymentReference || '',
      invoiceStatus: form.invoiceStatus || 'pending',
      receiptStatus: form.receiptStatus || (calculated.receivedAmount > 0 ? 'issued' : 'pending'),
      refundStatus: calculated.refundAmount > 0 ? 'processed' : (form.refundStatus || 'none'),
      invoiceNo,
      receiptNo,
      refundNo,
      accountingStatus: calculated.outstandingAmount === 0 && calculated.supplierBalance === 0 ? 'closed' : 'open',
      paymentNotes: form.paymentNotes || '',
      notes: form.notes || '',
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      createdBy: form.createdBy || user.uid
    };

    setBusy(true);
    try {
      if (editing) {
        await updateDoc(doc(db, 'bookings', editing.id), payload);
      } else {
        await addDoc(collection(db, 'bookings'), { ...payload, createdAt: serverTimestamp() });
      }
      setOpen(false);
      await loadAll();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to save booking.');
    } finally {
      setBusy(false);
    }
  }

  function openBookingDocuments() {
    window.history.pushState(null, '', '/documents?ownerType=booking');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Booking Operations</p>
          <h1>Booking Pro</h1>
          <p>Client, supplier, payment, invoice, receipt and supplier payable tracking in one booking record.</p>
        </div>
        <button className="btn primary" title="Add new booking" onClick={startAdd}><Plus size={18} /> Add Booking</button>
      </div>

      {message && <div className="error-box" style={{ marginBottom: 20 }}>{message}</div>}

      <div className="reminder-top-grid">
        <section className="card reminder-card"><h3>Total Sell</h3><p className="muted"><b>${totals.sell.toFixed(2)}</b></p></section>
        <section className="card reminder-card"><h3>Received</h3><p className="muted"><b>${totals.received.toFixed(2)}</b></p></section>
        <section className="card reminder-card"><h3>Outstanding</h3><p className="muted"><b>${totals.outstanding.toFixed(2)}</b></p></section>
        <section className="card reminder-card"><h3>Supplier Payable</h3><p className="muted"><b>${totals.supplierBalance.toFixed(2)}</b></p></section>
        <section className="card reminder-card"><h3>Profit</h3><p className="muted"><b>${totals.profit.toFixed(2)}</b></p></section>
      </div>

      <div className="toolbar" style={{ marginTop: 20 }}>
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: 16, top: 18, color: '#66758a' }} />
          <input style={{ paddingLeft: 44 }} placeholder="Search booking, client, supplier, sub-service, destination" value={search} onChange={(event) => setSearch(event.target.value)} />
        </div>
      </div>

      <section className="card table-card" style={{ marginTop: 20 }}>
        <table>
          <thead>
            <tr>
              <th>Booking</th>
              <th>Client</th>
              <th>Supplier</th>
              <th>Service</th>
              <th>Sell / Received</th>
              <th>Net / Paid</th>
              <th>Profit</th>
              <th>Status</th>
              <th>Docs</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.length === 0 && <tr><td colSpan={10} className="empty">No bookings found.</td></tr>}
            {filteredBookings.map((booking) => (
              <tr key={booking.id}>
                <td><strong>{booking.bookingCode}</strong><br/><small>{booking.destination || '-'}</small></td>
                <td>{booking.clientName}<br/><small>{booking.clientPhone || '-'}</small></td>
                <td>{booking.supplierName || '-'}</td>
                <td>{booking.serviceType}<br/><small>{booking.subServiceType}</small></td>
                <td>${booking.sellAmount.toFixed(2)}<br/><small>Rec: ${booking.receivedAmount.toFixed(2)} | {booking.paymentMode}</small></td>
                <td>${booking.netAmount.toFixed(2)}<br/><small>Paid: ${booking.supplierPaidAmount.toFixed(2)}</small></td>
                <td>${booking.profit.toFixed(2)}<br/><small>Due: ${booking.outstandingAmount.toFixed(2)}</small></td>
                <td><span className="pill">{booking.bookingStatus}</span><br/><small>{booking.paymentStatus} / {booking.accountingStatus}</small></td>
                <td><small>INV: {booking.invoiceNo || '-'}</small><br/><small>RCT: {booking.receiptNo || '-'}</small></td>
                <td className="actions">
                  <button title="View booking" onClick={() => setViewing(booking)}><Eye size={16}/></button>
                  <button title="Edit booking" onClick={() => startEdit(booking)}><Edit size={16}/></button>
                  <button title="Booking documents" onClick={openBookingDocuments}><FileText size={16}/></button>
                  <button title="Delete booking" onClick={async () => { if (window.confirm('Delete this booking? Accounting linked values will no longer appear.')) { await deleteDoc(doc(db, 'bookings', booking.id)); await loadAll(); } }}><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {open && (
        <div className="modal-backdrop">
          <div className="modal large">
            <div className="modal-head">
              <h2>{editing ? 'Edit Booking' : 'Add Booking'}</h2>
              <button className="icon-only" title="Close" onClick={() => setOpen(false)}>×</button>
            </div>

            <div className="form-section-title">Client + Supplier</div>
            <div className="form-grid">
              <label>Search Client<input value={clientSearch} placeholder="Search client name/phone" onChange={(event) => setClientSearch(event.target.value)} /></label>
              <label>Client *<select value={form.clientId || ''} onChange={(event) => selectClient(event.target.value)}><option value="">Select Client</option>{filteredClients.map((client) => <option key={client.id} value={client.id}>{client.name} — {client.phone || client.email}</option>)}</select></label>
              <label>Department<select value={form.department || 'package'} onChange={(event) => setForm({ ...form, department: event.target.value, supplierId: '', supplierName: '' })}>{departments.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label>Search Supplier<input value={supplierSearch} placeholder="Search supplier by selected department" onChange={(event) => setSupplierSearch(event.target.value)} /></label>
              <label>Supplier<select value={form.supplierId || ''} onChange={(event) => selectSupplier(event.target.value)}><option value="">Select Supplier</option>{filteredSuppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name} — {supplier.department}</option>)}</select></label>
              <label>Service Type *<select value={form.serviceType || 'package'} onChange={(event) => updateServiceType(event.target.value)}>{serviceTypes.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label>Sub-Service *<select value={form.subServiceType || ''} onChange={(event) => setForm({ ...form, subServiceType: event.target.value })}>{subServices.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label>Destination<input value={form.destination || ''} onChange={(event) => setForm({ ...form, destination: event.target.value })}/></label>
              <label>Passengers<input type="number" value={form.passengers === 0 ? '' : (form.passengers ?? '')} onChange={(event) => setForm({ ...form, passengers: Number(event.target.value || 0) })}/></label>
            </div>

            <div className="form-section-title">Travel Dates</div>
            <div className="form-grid">
              <label>Travel Start<input type="date" value={form.travelStartDate || ''} onChange={(event) => setForm({ ...form, travelStartDate: event.target.value })}/></label>
              <label>Travel End<input type="date" value={form.travelEndDate || ''} onChange={(event) => setForm({ ...form, travelEndDate: event.target.value })}/></label>
              <label>Return Date<input type="date" value={form.returnDate || ''} onChange={(event) => setForm({ ...form, returnDate: event.target.value })}/></label>
            </div>

            <div className="form-section-title">Accounting + Payment</div>
            <div className="form-grid">
              <label>Sell Amount<input type="number" value={form.sellAmount === 0 ? '' : (form.sellAmount ?? '')} onChange={(event) => setForm({ ...form, sellAmount: event.target.value === '' ? 0 : Number(event.target.value) })}/></label>
              <label>Received Amount<input type="number" value={form.receivedAmount === 0 ? '' : (form.receivedAmount ?? '')} onChange={(event) => setForm({ ...form, receivedAmount: event.target.value === '' ? 0 : Number(event.target.value) })}/></label>
              <label>Payment Mode<select value={form.paymentMode || 'zelle'} onChange={(event) => setForm({ ...form, paymentMode: event.target.value as PaymentMode })}>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label>Payment Reference<input value={form.paymentReference || ''} onChange={(event) => setForm({ ...form, paymentReference: event.target.value })}/></label>
              <label>Net Amount<input type="number" value={form.netAmount === 0 ? '' : (form.netAmount ?? '')} onChange={(event) => setForm({ ...form, netAmount: event.target.value === '' ? 0 : Number(event.target.value) })}/></label>
              <label>Supplier Paid<input type="number" value={form.supplierPaidAmount === 0 ? '' : (form.supplierPaidAmount ?? '')} onChange={(event) => setForm({ ...form, supplierPaidAmount: event.target.value === '' ? 0 : Number(event.target.value) })}/></label>
              <label>Supplier Payment Mode<select value={form.supplierPaymentMode || 'bank_transfer'} onChange={(event) => setForm({ ...form, supplierPaymentMode: event.target.value as PaymentMode })}>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label>Supplier Payment Reference<input value={form.supplierPaymentReference || ''} onChange={(event) => setForm({ ...form, supplierPaymentReference: event.target.value })}/></label>
              <label>Refund Amount<input type="number" value={form.refundAmount === 0 ? '' : (form.refundAmount ?? '')} onChange={(event) => setForm({ ...form, refundAmount: event.target.value === '' ? 0 : Number(event.target.value) })}/></label>
              <label>Booking Status<select value={form.bookingStatus || 'draft'} onChange={(event) => setForm({ ...form, bookingStatus: event.target.value as BookingStatus })}><option>draft</option><option>confirmed</option><option>cancelled</option><option>completed</option></select></label>
              <label>Invoice Status<select value={form.invoiceStatus || 'pending'} onChange={(event) => setForm({ ...form, invoiceStatus: event.target.value })}><option>pending</option><option>issued</option><option>not_required</option></select></label>
              <label>Receipt Status<select value={form.receiptStatus || 'pending'} onChange={(event) => setForm({ ...form, receiptStatus: event.target.value })}><option>pending</option><option>issued</option><option>not_required</option></select></label>
              <label>Invoice No<input value={form.invoiceNo || ''} onChange={(event) => setForm({ ...form, invoiceNo: event.target.value })}/></label>
              <label>Receipt No<input value={form.receiptNo || ''} onChange={(event) => setForm({ ...form, receiptNo: event.target.value })}/></label>
              <label>Refund No<input value={form.refundNo || ''} onChange={(event) => setForm({ ...form, refundNo: event.target.value })}/></label>
              <label>Accounting Status<select value={form.accountingStatus || 'open'} onChange={(event) => setForm({ ...form, accountingStatus: event.target.value })}><option>open</option><option>closed</option><option>review</option></select></label>
              <label className="span-3">Payment Notes<textarea value={form.paymentNotes || ''} onChange={(event) => setForm({ ...form, paymentNotes: event.target.value })}/></label>
              <label className="span-3">Booking Notes<textarea value={form.notes || ''} onChange={(event) => setForm({ ...form, notes: event.target.value })}/></label>
            </div>

            <div className="modal-foot">
              <button className="btn" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn primary" disabled={busy} onClick={saveBooking}>{busy ? 'Saving...' : 'Save Booking'}</button>
            </div>
          </div>
        </div>
      )}

      {viewing && (
        <div className="modal-backdrop">
          <div className="modal large">
            <div className="modal-head">
              <h2>{viewing.bookingCode}</h2>
              <button className="icon-only" title="Close" onClick={() => setViewing(null)}>×</button>
            </div>
            <div className="form-section-title">Booking Snapshot</div>
            <div className="form-grid">
              <div><b>Client</b><p>{viewing.clientName}</p></div>
              <div><b>Supplier</b><p>{viewing.supplierName || '-'}</p></div>
              <div><b>Service</b><p>{viewing.serviceType} / {viewing.subServiceType}</p></div>
              <div><b>Destination</b><p>{viewing.destination || '-'}</p></div>
              <div><b>Travel</b><p>{viewing.travelStartDate || '-'} to {viewing.returnDate || viewing.travelEndDate || '-'}</p></div>
              <div><b>Sell / Received</b><p>${viewing.sellAmount.toFixed(2)} / ${viewing.receivedAmount.toFixed(2)}</p></div>
              <div><b>Net / Supplier Paid</b><p>${viewing.netAmount.toFixed(2)} / ${viewing.supplierPaidAmount.toFixed(2)}</p></div>
              <div><b>Profit</b><p>${viewing.profit.toFixed(2)}</p></div>
              <div><b>Outstanding</b><p>${viewing.outstandingAmount.toFixed(2)}</p></div>
              <div><b>Supplier Balance</b><p>${viewing.supplierBalance.toFixed(2)}</p></div>
              <div><b>Status</b><p>{viewing.bookingStatus} / {viewing.paymentStatus}</p></div>
              <div><b>Invoice</b><p>{viewing.invoiceNo || '-'} / {viewing.invoiceStatus}</p></div>
              <div><b>Receipt</b><p>{viewing.receiptNo || '-'} / {viewing.receiptStatus}</p></div>
              <div><b>Refund</b><p>{viewing.refundNo || '-'} / {viewing.refundStatus}</p></div>
              <div className="span-3"><b>Payment Notes</b><p>{viewing.paymentNotes || '-'}</p></div>
              <div className="span-3"><b>Notes</b><p>{viewing.notes || '-'}</p></div>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={openBookingDocuments}><FileText size={16}/> Documents</button>
              <button className="btn" onClick={() => window.print()}><ReceiptText size={16}/> Print</button>
              <button className="btn primary" onClick={() => startEdit(viewing)}><Edit size={16}/> Edit Booking</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
